import type { SegmentUrlResolver } from '@mimir/client-common/util/segmentUrlCache';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { BufferFetching, FragmentedMp4Provider } from './fetching';
import type { FlacDownmixConfig } from '@mimir/common/mpegts/transmuxUtility';
import type { AudioTransform, FlacDownmix } from './audioTransform';
import { invariant } from '@mimir/common/util/invariant';
import cloneDeep from 'lodash/cloneDeep';
import type { ObservableState } from '@mimir/common/util/observable';
import type { SegmentData, TransmuxerOp } from './transmuxerOp';
import type { TransmuxingResult, TransmuxingWorkerOp } from './transmuxingWorkerOp';
import { SimpleWorker } from './simpleWorker';
import { TransmuxingWebWorker } from './transmuxingWebWorker';
import type { FragmentedMp4Buffers } from '@mimir/common/mp4/fragmentedMp4Types';
import type { Cleanable } from '@mimir/common/util/cleanable';



/**
 * This remuxer is specialized for Flac downmix.
 */
export class FlacDownmixTSRemuxer implements FragmentedMp4Provider {

  private transmuxer: TransmuxerOp;

  constructor(
    private urlResolver: SegmentUrlResolver,
    private fetching: BufferFetching,
    readonly itemId: string,
    private audioTrackCount: ObservableState<number | null>,
    private playerId: string,
    private audioOnly: boolean,
  ) {
    this.transmuxer = this.createTransmuxer();
  }


  private createTransmuxer(): TransmuxerOp {
    const worker = window.Worker ? new TransmuxingWebWorker(this.audioOnly) : new SimpleWorker(this.playerId, this.audioOnly);
    return new WorkerTransmuxer(worker);
  }

  private getFlacDownmixConfig(audioTransform: FlacDownmix): FlacDownmixConfig {
    return {
      playerId: this.playerId,
      mixMatrix: cloneDeep(audioTransform.mixMatrix),
      audioChannelCount: this.audioTrackCount,
    };
  }


  private async getBuffer(streamId: StreamRoleId, segmentTime: number): Promise<SegmentData> {
    const url = await this.urlResolver.getMediaSegmentUrl(this.itemId, streamId, segmentTime);
    return {
      buffer: await this.fetching.getBuffer(url),
      url: url,
      segmentIndex: segmentTime,
    };
  }


  async getMediaSegmentBuffers(_needInitialization: boolean, streamId: StreamRoleId, segmentTime: number, audioTransform: AudioTransform): Promise<FragmentedMp4Buffers> {
    invariant(audioTransform.type === 'flacDownmix', 'Should have FlacDownmix transform');
    try {
      return await this.getMediaSegmentBuffersInternal(streamId, segmentTime, audioTransform);
    } catch (err) {
      // TODO: is this fallback retry still needed ?
      console.error('Transmux error. Trying to recover', err);
    }

    this.transmuxer = this.createTransmuxer();
    return await this.getMediaSegmentBuffersInternal(streamId, segmentTime, audioTransform);
  }


  private async getBuffers(streamId: StreamRoleId, indexes: number[]): Promise<SegmentData[]> {
    return await Promise.all(indexes.map(index => this.getBuffer(streamId, index)));
  }


  /**
   * This function is responsible for figuring out and fetching the segments
   * that may be needed in order to decode correctly the segment corresponding
   * to the given segment time.
   *
   * In order to decode that segment correctly, we need the segment zero if
   * the transmuxer is not initialized yet.
   *
   * Also, if the segment is not the first one, we need to fetch the segment
   * just before it, because we may need to pass this segment into the transmuxer
   * in order to avoid audio artifacts at the segment boundaries. Note that this
   * previous segment N may not be necessary if the this segment N is that last one
   * decoded by the transmuxer.
   *
   * This strategy relies on the browser caching that should be efficient enough to
   * make any fetch operation very fast if this segment was fetched before.
   */
  private async fetchSegments(streamId: StreamRoleId, segmentTime: number): Promise<SegmentData[]> {
    const segmentsToFetch = new Set<number>();
    // We always want to get the segment we need
    segmentsToFetch.add(segmentTime);

    // If we have not initialized the transmuxer yet, we need
    // to feed it with the segment 0 first
    if (!this.transmuxer.isInitialized()) {
      segmentsToFetch.add(0);
    }

    if (segmentTime > 0) {
      // If the last decoded segment is not the one immediately before
      // the segment N we want to decode now, we need to re-initialize the AAC
      // decoders with the last AAC data from the segment N-1 in order
      // to avoid audio glitches at segment boundary.
      segmentsToFetch.add(segmentTime - 1);
    }

    return await this.getBuffers(streamId, [...segmentsToFetch]);
  }


  async getMediaSegmentBuffersInternal(streamId: StreamRoleId, segmentTime: number, audioTransform: FlacDownmix): Promise<FragmentedMp4Buffers> {
    invariant(audioTransform.type === 'flacDownmix', 'Should have FlacDownmix transform');
    const segments = await this.fetchSegments(streamId, segmentTime);
    const flacDownmixConfig = this.getFlacDownmixConfig(audioTransform);
    return await this.transmuxer.transmux(flacDownmixConfig, segmentTime, segments);
  }

  cleanup() {
    this.transmuxer.cleanup();
  }
}



/**
 * This implementation posts transmux requests to a worker.
 */
class WorkerTransmuxer implements TransmuxerOp, Cleanable {

  private initialized = false;

  constructor(private worker: TransmuxingWorkerOp) {}

  async transmux(flacDownmixConfig: FlacDownmixConfig, segmentToDecode: number, segments: SegmentData[]): Promise<FragmentedMp4Buffers> {
    return new Promise<FragmentedMp4Buffers>((resolve, reject) => {
      this.worker.post(flacDownmixConfig.mixMatrix, segmentToDecode,segments, (result: TransmuxingResult) => {
        if (result.type === 'error') {
          reject(result.errorMsg);
        } else {
          // The transmuxer is initialized as soon as a first transmuxing operation
          // has been executed
          this.initialized = true;
          flacDownmixConfig.audioChannelCount.value = result.audioChannelCount;
          resolve(result.data);
        }
      });
    });
  }


  isInitialized(): boolean {
    return this.initialized;
  }

  cleanup() {
    this.worker.cleanup();
  }
}
